import { QueryAggregator } from "@/api/utils/QueryAggregator"
import { JuniferService } from "@/services/JuniferService"
import { TransactionService } from "@/services/TransactionService"
import { DirectDebitService } from "@/services/DirectDebitService"
import { PaymentScheduleService } from "@/services/PaymentScheduleService"
import { BillRequestService } from "@/services/BillRequestService"
import { ReviewPeriodService } from "@/services/ReviewPeriodService"
import { IJuniferTicket } from "@/models/juniferTickets/juniferTicket.types"
import { IPaymentSchedule } from "@/models/paymentSchedule/paymentSchedule.types"
import { ITransaction } from "@/models/transaction/transaction.types"
import { formatDate, getDateRange } from "@/helpers/dateHelper"
import { IBillRequest } from "@/models/billRequest/billRequest.types"
import { IReviewPeriod } from "@/models/reviewPeriod/reviewPeriod.types"
import { MeterpointService } from "@/services/MeterpointService"
import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
import { Status } from "@/types/Status"
import dayjs from "dayjs"
import { MeterReadingsService } from "@/services/operations/MeterReadingsService"
import { IOutboundPhotoRequestResponse } from "@/models/outboundPhoto/outboundPhoto.types"
import { hasOpenComplaintOnAccount } from "./hasOpenComplaintOnAccount"
import { getLatestDefaultNoticeTicket } from "./getDefaultNotice"
import {
  defaultStepCode,
  preDefaultClosedStepCode,
  preDefaultStepCode,
  preSatisfiedDefaultClosedStepCode,
  preSatisfiedDefaultStepCode,
} from "@/constants/defaultStepCodes"
import { getRtsMeters } from "./getRtsMeters"
import { getSmartMetersEligibleForExchange } from "./getSmartMetersEligibleForExchange"
import {
  RegistrationCreditChecksService,
  RegistrationCreditCheckStatusResult,
} from "@/services/operations/RegistrationCreditChecksService"
import { EnergyUsageService } from "@/services/EnergyUsageService"
import { QueryResult } from "@/api/utils/types"
import { IEnergyUsage } from "@/models/energyUsage/energyUsage.types"
import { AccountService } from "@/services/AccountService"

export enum UsageType {
  Daily = "daily",
  HalfHourly = "hh",
}

export interface HealthCheckInfo {
  status: Status
  title?: string
  type?: UsageType
}

export interface IAccountHealthOverview {
  meterReadings: HealthCheckInfo[]
  withinRenewalPeriod: HealthCheckInfo[]
  failedPayments: HealthCheckInfo[]
  hasOpenComplaint: HealthCheckInfo[]
  directDebitMandates: HealthCheckInfo[]
  paymentSchedules: HealthCheckInfo[]
  failedBills: HealthCheckInfo[]
  underReviewPeriods: HealthCheckInfo[]
  openJuniferTickets: HealthCheckInfo[]
  openOutboundRequest: HealthCheckInfo[]
  defaultStatus: HealthCheckInfo[]
  rtsMeters: HealthCheckInfo[]
  smartMetersEligibleForExchange: HealthCheckInfo[]
  hasSecurityDepositRequest: HealthCheckInfo[]
  energyUsage: HealthCheckInfo[]
}
const DAILY_USAGE_LOOKBACK_DAYS = 7
const HH_USAGE_LOOKBACK_DAYS = 1

const { from: fromDaily, to: toDaily } = getDateRange(DAILY_USAGE_LOOKBACK_DAYS)
const { from: fromHh, to: toHh } = getDateRange(HH_USAGE_LOOKBACK_DAYS)

export const getAccountHealthChecks = (accountNumber: string) => {
  const queries = new QueryAggregator()
  const coreIdQuery = AccountService.getCoreIdByAccountNumber(accountNumber)
  const hasOpenComplaintsQuery = hasOpenComplaintOnAccount(accountNumber)
  const transactionsQuery = TransactionService.getForAccount(coreIdQuery.data)
  const juniferTicketsQuery = JuniferService.getTicketsForAccount(
    coreIdQuery.data,
  )
  const hasActiveDDQuery = DirectDebitService.hasActiveDirectDebit(
    coreIdQuery.data,
  )
  const paymentSchedulesQuery =
    PaymentScheduleService.getForAccount(accountNumber)
  const billRequest = BillRequestService.getForAccount(coreIdQuery.data)
  const meterpointsQuery = MeterpointService.getForAccount(accountNumber)
  const underReviewPeriodsQuery = ReviewPeriodService.getForAccount(
    coreIdQuery.data,
  )
  const outboundRequestQuery =
    MeterReadingsService.getOutboundPhotoRequest(accountNumber)
  const creditCheckStatusQuery =
    RegistrationCreditChecksService.getCreditCheckStatus(accountNumber)
  const accountQuery = AccountService.getAccount(accountNumber)
  const accountId = accountQuery.data?.id ?? null

  const dailyUsageQuery = EnergyUsageService.getDailyUsage(
    accountId,
    fromDaily,
    toDaily,
  )

  const hhUsageQuery = EnergyUsageService.getHalfHourlyUsage(
    accountId,
    fromHh,
    toHh,
  )

  queries.add(coreIdQuery)
  queries.add(dailyUsageQuery)
  queries.add(hhUsageQuery)
  queries.add(hasOpenComplaintsQuery)
  queries.add(juniferTicketsQuery)
  queries.add(transactionsQuery)
  queries.add(hasActiveDDQuery)
  queries.add(paymentSchedulesQuery)
  queries.add(billRequest)
  queries.add(underReviewPeriodsQuery)
  queries.add(outboundRequestQuery)
  queries.add(creditCheckStatusQuery)

  const rtsMetersQuery = getRtsMeters(accountNumber)
  queries.add(rtsMetersQuery)
  const hasRtsMeters = rtsMetersQuery.data?.hasRtsMeters
  const hasUpcomingMeterExchange = rtsMetersQuery.data?.hasUpcomingMeterExchange

  const smartMetersEligibleForExchangeQuery =
    getSmartMetersEligibleForExchange(accountNumber)
  queries.add(smartMetersEligibleForExchangeQuery)
  const smartMetersEligibleForExchange =
    smartMetersEligibleForExchangeQuery.data?.smartMetersEligibleForExchange
  const hasUpcomingSmartMeterExchange =
    smartMetersEligibleForExchangeQuery.data?.hasUpcomingMeterExchange

  const healthChecks: IAccountHealthOverview = {
    meterReadings: checkMeterReadings(meterpointsQuery.data),
    withinRenewalPeriod: checkRenewalPeriodJuniferTickets(
      juniferTicketsQuery.data ?? [],
    ),
    failedPayments: checkFailedPayments(transactionsQuery.data),
    hasOpenComplaint: checkHasOpenComplaint(hasOpenComplaintsQuery.data),
    directDebitMandates: checkDDMandates(hasActiveDDQuery.data),
    paymentSchedules: checkActivePaymentSchedules(paymentSchedulesQuery.data),
    failedBills: checkFailedBills(billRequest.data),
    underReviewPeriods: checkUnderReviewPeriods(underReviewPeriodsQuery.data),
    openJuniferTickets: checkOpenJuniferTickets(juniferTicketsQuery.data ?? []),
    openOutboundRequest: checkOutboundRequests(outboundRequestQuery.data),
    defaultStatus: checkLatestDefaultNotice(juniferTicketsQuery.data ?? []),
    rtsMeters: checkRtsMeters(hasRtsMeters, hasUpcomingMeterExchange),
    smartMetersEligibleForExchange: checkSmartMetersEligibleForExchange(
      smartMetersEligibleForExchange,
      hasUpcomingSmartMeterExchange,
    ),
    hasSecurityDepositRequest: checkSecurityDepositRequests(
      creditCheckStatusQuery.data,
    ),
    energyUsage: checkEnergyUsage(dailyUsageQuery, hhUsageQuery),
  }

  return queries.setData(healthChecks).getAggregate()
}

function checkOutboundRequests(
  outboundRequests: IOutboundPhotoRequestResponse[],
): HealthCheckInfo[] {
  const hasOutboundRequest = outboundRequests?.length > 0
  return hasOutboundRequest
    ? [
        {
          status: new Status("critical", "Open"),
        },
      ]
    : []
}
export interface ButtonConfig {
  title: string
  onClick: () => void
  type?: "text" | "primary" | "secondary"
  prependIcon?: string
}

export interface HealthCheckInfo {
  status: Status
  failedCheckButton?: ButtonConfig
  passedCheckButton?: ButtonConfig
}
function checkRtsMeters(
  hasRtsMeters: boolean,
  hasUpcomingMeterExchange: boolean | undefined,
): HealthCheckInfo[] {
  if (!hasRtsMeters) {
    return []
  }
  if (hasUpcomingMeterExchange) {
    return [
      {
        status: new Status("caution", "RTS meter exchange booked"),
      },
    ]
  }
  return [
    {
      status: new Status("critical", "RTS meters on site"),
    },
  ]
}

function checkSmartMetersEligibleForExchange(
  meters:
    | Array<{
        meterSerialNumber: string
        exchangeEligibilityReason: string | null
      }>
    | undefined,
  hasUpcomingMeterExchange: boolean | undefined,
): HealthCheckInfo[] {
  if (!meters?.length) {
    return []
  }
  const reasons =
    [
      ...new Set(
        meters.map((m) => m.exchangeEligibilityReason).filter(Boolean),
      ),
    ].join(", ") || undefined
  const action = hasUpcomingMeterExchange ? "booked" : "needed"
  const statusType = hasUpcomingMeterExchange ? "caution" : "critical"
  const label = reasons
    ? `Meter exchange ${action} (${reasons})`
    : `Meter exchange ${action}`
  return [{ status: new Status(statusType, label) }]
}

function checkMeterReadings(meterpoints: IMeterpoint[]): HealthCheckInfo[] {
  return meterpoints.map((meterpoint) => {
    return {
      status: new Status(
        meterpoint.latestReadStatus.type,
        meterpoint.latestReadStatus.text,
      ),
      title: `${meterpoint.meterpointType} - ${meterpoint.identifier}`,
    }
  })
}

function checkRenewalPeriodJuniferTickets(
  juniferTickets: IJuniferTicket[],
): HealthCheckInfo[] {
  const isInRenewalPeriod = juniferTickets.some(
    (juniferTicket: IJuniferTicket) => juniferTicket.isWithinRenewalPeriod,
  )

  return [
    {
      status: isInRenewalPeriod
        ? new Status("critical", "In renewal period")
        : new Status("normal", "Not in renewal period"),
    },
  ]
}

function checkFailedPayments(transactions: ITransaction[]): HealthCheckInfo[] {
  // Check for failed payments in the last 60 days
  const acceptedStatuses = ["Accepted", "Pending", "Authorising"]
  const thresholdDate = dayjs().subtract(60, "days")

  const failedPayments = transactions.filter((transaction: ITransaction) => {
    return (
      !acceptedStatuses.includes(transaction.status.text) &&
      transaction.isPayment &&
      transaction.createdDttm.isAfter(thresholdDate)
    )
  })

  if (failedPayments.length > 1) {
    return [
      {
        status: new Status(
          "critical",
          failedPayments.length + " failed payments",
        ),
      },
    ]
  }

  if (failedPayments.length === 1) {
    return [
      {
        status: new Status(
          "critical",
          "Failed payment on " + formatDate(failedPayments[0].createdDttm),
        ),
      },
    ]
  }

  return [
    {
      status: new Status("normal", "No failed payments"),
    },
  ]
}

function checkHasOpenComplaint(
  hasOpenComplaint: boolean | null,
): HealthCheckInfo[] {
  if (hasOpenComplaint) {
    return [
      {
        status: new Status("critical", "Has open complaint(s)"),
      },
    ]
  }

  return [
    {
      status: new Status("normal", "No open complaints"),
    },
  ]
}

function checkDDMandates(hasActiveDirectDebit: boolean): HealthCheckInfo[] {
  return [
    {
      status: hasActiveDirectDebit
        ? new Status("normal", "Active")
        : new Status("critical", "Inactive"),
    },
  ]
}

function checkActivePaymentSchedules(
  schedules: IPaymentSchedule[],
): HealthCheckInfo[] {
  const activeSchedules = schedules.filter((schedule) => schedule.isActive)

  if (!activeSchedules.length) {
    return [
      {
        status: new Status("critical", "No payment schedule"),
      },
    ]
  }

  return activeSchedules.map((schedule: IPaymentSchedule) => {
    return {
      status: new Status("normal", schedule.amountFormatted),
    }
  })
}

function checkFailedBills(billRequests: IBillRequest[]): HealthCheckInfo[] {
  const failedBillsCount = billRequests.filter((b) => b.isFailed).length

  if (failedBillsCount) {
    return [
      {
        status: new Status(
          "critical",
          failedBillsCount + " failed bill" + (failedBillsCount > 1 ? "s" : ""),
        ),
      },
    ]
  }

  return [
    {
      status: new Status("normal", "No failed bills"),
    },
  ]
}

function checkUnderReviewPeriods(
  reviewPeriods: IReviewPeriod[],
): HealthCheckInfo[] {
  const activeReviewPeriods = reviewPeriods.filter(
    (period: IReviewPeriod) => period.isActive,
  )

  if (!activeReviewPeriods.length) {
    return [
      {
        status: new Status("normal", "No account reviews"),
      },
    ]
  }

  return activeReviewPeriods.map((review) => ({
    status: new Status("critical", "In review: " + review.reason),
  }))
}

function checkOpenJuniferTickets(
  juniferTickets: IJuniferTicket[],
): HealthCheckInfo[] {
  const openTicketsCount = juniferTickets.filter(
    (ticket: IJuniferTicket) => ticket.isOpenOperationalTicket,
  ).length

  if (openTicketsCount) {
    return [
      {
        status: new Status(
          "critical",
          openTicketsCount +
            " open Junifer ticket" +
            (openTicketsCount > 1 ? "s" : ""),
        ),
      },
    ]
  }

  return [
    {
      status: new Status("normal", "No open Junifer tickets"),
    },
  ]
}

function checkLatestDefaultNotice(
  juniferTickets: IJuniferTicket[],
): HealthCheckInfo[] {
  const latestDefaultNotice = getLatestDefaultNoticeTicket(juniferTickets)

  if (!latestDefaultNotice) {
    return [
      {
        status: new Status("normal", "No default tickets"),
      },
    ]
  }

  const daysFromNow = dayjs().diff(latestDefaultNotice.createdDate, "days")

  const stepCodeStatusMap = new Map<string, HealthCheckInfo>([
    [
      preDefaultStepCode,
      {
        status: new Status(
          "critical",
          `Pre-default notice sent - ${daysFromNow} days`,
        ),
      },
    ],
    [
      preDefaultClosedStepCode,
      {
        status: new Status("normal", "Default Satisfied"),
      },
    ],
    [
      defaultStepCode,
      {
        status: new Status("critical", "Account in default"),
      },
    ],
    [
      preSatisfiedDefaultStepCode,
      {
        status: new Status("critical", "Default pre-satisfied"),
      },
    ],
    [
      preSatisfiedDefaultClosedStepCode,
      {
        status: new Status("normal", "Default satisfied"),
      },
    ],
    [
      "default",
      {
        status: new Status("normal", "No default tickets"),
      },
    ],
  ])

  return [
    stepCodeStatusMap.get(latestDefaultNotice.stepCode) ??
      (stepCodeStatusMap.get("default") as HealthCheckInfo),
  ]
}

function checkSecurityDepositRequests(
  data: RegistrationCreditCheckStatusResult,
): HealthCheckInfo[] {
  if (data?.status === "DEPOSIT_REQUESTED") {
    return [
      {
        status: new Status("critical", "Deposit not received"),
      },
    ]
  }
  return []
}

function checkEnergyUsage(
  dailyUsage: QueryResult<IEnergyUsage[] | null>,
  hhUsage: QueryResult<IEnergyUsage[] | null>,
): HealthCheckInfo[] {
  const hasDaily = !!(
    !dailyUsage.isError &&
    !!dailyUsage.data?.length &&
    !!dailyUsage.data?.every((meterpoint) => meterpoint.hasValues)
  )
  const hasHh = !!(
    !hhUsage.isError &&
    !!hhUsage.data?.length &&
    !!hhUsage.data?.every((meterpoint) => meterpoint.hasValues)
  )

  return [
    {
      status: hasDaily
        ? new Status("normal", "Daily usage data available")
        : new Status("caution", "No recent daily usage data"),
      title: "Daily usage",
    },
    {
      status: hasHh
        ? new Status("normal", "Half-hourly usage data available")
        : new Status("caution", "No recent half-hourly usage data"),
      title: "Half-hourly usage",
    },
  ]
}
